import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Typography, Stack, Box } from '@mui/material';
import { IconChannelsOrder, IconEye } from '../../../../../../assets/popup-nav-icons';
import { IconNoInstallation } from '../../../../../../assets/status-screen-icons/icon-no-installation';
import { Button, Header, PopUpNav, PopUpNavLink } from '../../../../../../components';
import { useInstallationContext } from '../../../../../../context/installation';
import { ROUTES } from '../../../../../../routes';
import { ChannelBox } from '../../../../components/channel';
import { GroupsSortChannelsBody } from '../../../group-sort-channels/components/body';
import { useMeasurementsDashboard } from '../../hooks/use-measurements';

export const DashboardMeasurements: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { t: tl } = useTranslation('channel-list');
  const { t: tc } = useTranslation('common');
  const { selectedInstallationId } = useInstallationContext();
  const {
    channels,
    onChannelToggle,
    handleVisibilitySave,
    allChannelsHidden,
    optionsVisible,
    visibleMode,
    setVisibleMode,
    meterChannels,
    orderMode,
    setOrderMode,
    meterGroupSortable,
    setMeterGroupSortable,
    setIsValid,
    handleOrderSave,
  } = useMeasurementsDashboard();

  const navLinks: PopUpNavLink[] = [
    {
      label: tl('editVisibility'),
      icon: <IconEye />,
      onClick: () => setVisibleMode(true),
    },
    ...(channels.length
      ? [
          {
            onClick: () => setOrderMode(true),
            label: t('channelsOrder'),
            icon: <IconChannelsOrder />,
          },
        ]
      : []),
  ];

  const handleEmptyAction = () => {
    if (allChannelsHidden) setVisibleMode(true);
    else navigate(ROUTES.MeterDevices());
  };

  const renderEmptyState = () => (
    <Stack alignItems="center">
      <IconNoInstallation />
      <Typography sx={{ textAlign: 'center', fontSize: '16px', marginBottom: 3 }}>
        {t(`statistics.${allChannelsHidden ? 'hidden' : 'empty'}`)}
      </Typography>
      <Button type="accent" size="small" center fullWidth={false} onClick={handleEmptyAction}>
        {allChannelsHidden ? tl('editVisibility') : t('statistics.seeDevices')}
      </Button>
    </Stack>
  );

  const renderVisibleModeView = () => (
    <>
      <Box className="p-b-16 grid grid--2 grid--small-gap">
        {meterChannels.map((ch) => (
          <ChannelBox
            key={ch.id}
            channel={ch}
            noRedirect
            {...(visibleMode && {
              isCheckbox: true,
              checked: !!channels.find(({ id }) => id === ch.id),
              onChannelToggle,
            })}
          />
        ))}
      </Box>
      <Button type="accent" size="small" center fullWidth={false} onClick={handleVisibilitySave}>
        {tc('buttons.save')}
      </Button>
    </>
  );

  const renderOrderMode = () => (
    <>
      {meterGroupSortable && (
        <Box className="p-b-24">
          <GroupsSortChannelsBody
            activeGroup={meterGroupSortable}
            setActiveGroup={setMeterGroupSortable}
            setIsValid={setIsValid}
          />
        </Box>
      )}
      <Button type="accent" size="small" center fullWidth={false} onClick={handleOrderSave}>
        {tc('buttons.save')}
      </Button>
    </>
  );

  const renderNormalView = () =>
    channels.length ? (
      <Box className="grid grid--2 grid--small-gap">
        {channels.map((channel) => (
          <ChannelBox key={channel.id} channel={channel} />
        ))}
      </Box>
    ) : (
      renderEmptyState()
    );

  const renderContent = () => {
    if (visibleMode) return renderVisibleModeView();
    if (orderMode) return renderOrderMode();
    return renderNormalView();
  };

  if (!selectedInstallationId || !meterGroupSortable?.id) return null;

  return (
    <Box marginBottom={6} paddingLeft={3} paddingRight={3}>
      <Header title={t('statistics.header')}>
        <PopUpNav links={optionsVisible ? navLinks : []} />
      </Header>

      {meterChannels.length ? renderContent() : renderEmptyState()}
    </Box>
  );
};
